import { useAlert } from '@weave/alert-system';
import {
  CheckIconSmall,
  ContentLoader,
  Heading,
  PendingIconSmall,
  Table,
  TableColumnConfig,
  Text,
} from '@weave/design-system';
import { theme } from '@weave/theme';
import React, { FC, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentLocationV2 } from 'redux/actions/location';
import { getParsedErrorMessage } from 'redux/axios';
import { useGetFastPassOnboardingActivity } from '../onboarding.queries';
import { css } from '@emotion/core';
import { formatDate } from 'helpers/date';
import { OnboardingActivityHistory } from '../onboarding.types';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { SendUserInviteEmailAction } from './SendUserInviteEmailAction';

dayjs.extend(duration);

const COLOR_INDIGO = '#6264F4';
const SORT_FALLBACK_DATE = '9999-12-31';

export const OnboardingActivity: FC<{
  showUserInviteAction?: boolean;
}> = ({ showUserInviteAction = true }) => {
  const location = useSelector(selectCurrentLocationV2);
  const alert = useAlert();

  const {
    data: onboardingActivityResponse,
    isLoading,
    isError,
    error,
  } = useGetFastPassOnboardingActivity(
    {
      slug: location?.slug,
    },
    {
      select: (response) => ({
        ...response,
        onboardingActivityHistory: response.onboardingActivityHistory
          ?.sort(
            ({ dateCompleted: dateCompletedA }, { dateCompleted: dateCompletedB }) =>
              new Date(dateCompletedA || SORT_FALLBACK_DATE).getTime() -
              new Date(dateCompletedB || SORT_FALLBACK_DATE).getTime()
          )
          .map((activity, index) => {
            let timeToComplete = activity.timeToComplete;
            const previousStepCompletedAt =
              response.onboardingActivityHistory?.[index - 1]?.dateCompleted;
            if (!timeToComplete && previousStepCompletedAt) {
              timeToComplete =
                formatTimeDuration(previousStepCompletedAt, new Date().toISOString()) ??
                '';
            }
            return {
              ...activity,
              timeToComplete,
            };
          }),
      }),
      retry: false,
    }
  );

  useEffect(() => {
    if (isError && error) {
      alert.error(getParsedErrorMessage(error) ?? 'Failed to load onboarding activity');
    }
  }, [isError, error]);

  const { startDate, lastActive } = useMemo(() => {
    const firstCompletedDate =
      onboardingActivityResponse?.onboardingActivityHistory?.find(
        ({ dateCompleted }) => dateCompleted
      )?.dateCompleted;
    const lastCompletedDate =
      onboardingActivityResponse?.onboardingActivityHistory?.findLast(
        (activity) => activity.dateCompleted
      )?.dateCompleted;
    return {
      startDate: firstCompletedDate ? formatDate(firstCompletedDate, 'lll') : '-',
      lastActive: lastCompletedDate ? formatDate(lastCompletedDate, 'lll') : '-',
    };
  }, [onboardingActivityResponse]);

  return (
    <div css={styles.container}>
      <ContentLoader show={isLoading} />
      {showUserInviteAction && <SendUserInviteEmailAction />}
      <Heading level={2}>Onboarding Activity</Heading>
      <div css={styles.summaryFieldsContainer}>
        <div css={styles.summaryField}>
          <Text size="medium" color="light">
            User
          </Text>
          <Text size="medium">{onboardingActivityResponse?.onboardingUser ?? '-'}</Text>
        </div>
        <div css={styles.summaryField}>
          <Text size="medium" color="light">
            Start Date
          </Text>
          <Text size="medium">{startDate}</Text>
        </div>
        <div css={styles.summaryField}>
          <Text size="medium" color="light">
            Last Active
          </Text>
          <Text size="medium">{lastActive}</Text>
        </div>
      </div>
      <OnboardingActivityTable
        isLoading={isLoading}
        activityData={onboardingActivityResponse?.onboardingActivityHistory}
      />
    </div>
  );
};

const OnboardingActivityTable: FC<{
  isLoading: boolean;
  activityData?: OnboardingActivityHistory[];
}> = ({ isLoading, activityData = [] }) => {
  const colConfig = useMemo<TableColumnConfig<OnboardingActivityHistory>[]>(() => {
    return [
      {
        Header: 'Step',
        accessor: ({ stepName }) => stepName,
        id: 'stepName',
        minWidth: 200,
      },
      {
        Header: 'Time to Complete',
        accessor: ({ timeToComplete, dateCompleted }) => ({
          dateCompleted,
          timeToComplete,
        }),
        cellRenderer: ({ timeToComplete, dateCompleted }) => (
          <TimeToCompleteChip duration={timeToComplete} isComplete={dateCompleted} />
        ),
        id: 'timeToComplete',
        width: 200,
        sortable: false,
      },
      {
        Header: 'Date Completed',
        accessor: ({ dateCompleted }) =>
          dateCompleted ? formatDate(dateCompleted, 'lll') : 'Not Completed',
        id: 'completedAt',
        minWidth: 200,
        sortable: false,
      },
      {
        Header: 'Completed By',
        accessor: ({ completedBy, dateCompleted, timeToComplete }) => ({
          dateCompleted,
          completedBy,
          timeToComplete,
        }),
        cellRenderer: ({ completedBy, dateCompleted, timeToComplete }) =>
          dateCompleted
            ? completedBy || 'Unknown'
            : timeToComplete
            ? 'Not Completed'
            : 'Not Started',
        id: 'completedBy',
        minWidth: 200,
        sortable: false,
      },
    ];
  }, []);

  return (
    <div css={styles.activityTableContainer}>
      <Text size="large" weight="bold" css={{ margin: 0 }}>
        Details
      </Text>
      <Table
        isLoading={isLoading}
        loadingRowsCount={3}
        data={activityData}
        colConfig={colConfig}
        styleConfig={{
          columns: [
            {
              id: '*',
              headerStyler: css`
                background-color: ${theme.colors.neutral5};
                padding: ${theme.spacing(1)};
                margin-bottom: ${theme.spacing(1)};
              `,
              cellStyler: (row) => css`
                background-color: ${theme.colors.neutral5};
                padding: ${theme.spacing(1)};
                border-bottom: none !important;
              `,
            },
          ],
        }}
      />
    </div>
  );
};

const TimeToCompleteChip: FC<{
  duration?: string;
  isComplete?: boolean;
}> = ({ isComplete = false, duration }) => {
  if (!duration && !isComplete) {
    return <span>Not Started</span>;
  }
  return (
    <div
      css={[
        styles.timeDurationChip,
        isComplete ? styles.completedChip : styles.activeChip,
      ]}
    >
      {isComplete ? (
        <CheckIconSmall size={14} />
      ) : (
        <PendingIconSmall size={14} css={{ color: COLOR_INDIGO }} />
      )}
      {!!duration && (
        <div
          css={{
            color: isComplete ? theme.colors.neutral70 : COLOR_INDIGO,
            fontSize: theme.font.size.medium,
          }}
        >
          {duration}
        </div>
      )}
    </div>
  );
};

const formatTimeDuration = (startTime: string, endTime?: string): string | null => {
  if (!startTime) {
    return null;
  }

  const start = dayjs(startTime);
  const end = endTime ? dayjs(endTime) : dayjs();
  const diff = dayjs.duration(end.diff(start));

  const days = Math.floor(diff.asDays());
  const hours = diff.hours();
  const minutes = diff.minutes();
  const seconds = diff.seconds();

  const timeUnits: string[] = [];

  if (days > 0) timeUnits.push(`${days}d`);
  if (hours > 0) timeUnits.push(`${hours}h`);
  if (minutes > 0) timeUnits.push(`${minutes}min`);
  if (seconds > 0) timeUnits.push(`${seconds}s`);

  // Return only the first two time units
  return timeUnits.slice(0, 2).join(' ') || '0s';
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
    position: relative;
  `,
  summaryFieldsContainer: css`
    display: flex;
    flex-direction: column;
    align-self: flex-start;
    min-width: 200px;
  `,

  summaryField: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${theme.spacing(1)};
  `,

  activityTableContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(2)};
    border-radius: 16px;
    border: 1px solid ${theme.colors.neutral20};
    background-color: ${theme.colors.neutral5};
  `,

  timeDurationChip: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    padding: ${theme.spacing(0.25, 0.5)};
    border-radius: ${theme.borderRadius.small};
  `,

  activeChip: css`
    background-color: ${theme.colors.white};
  `,

  completedChip: css`
    background-color: ${theme.colors.neutral20};
  `,
};
